import { Card, Colors, getRelativeTime, Heading, Text, useViewportBelow } from '@amelio/ui-library';

import type { CommentAuthor, QuestionComment } from '@/components/QuestionResultContainer';
import type {
    CommentThread,
    CommentThreadReply,
    ReviewAnswerCommentThreads,
} from '@/types/review/CommentThread';

import { AuthorSignaturePreview } from '@/components/AuthorSignaturePreview/AuthorSignaturePreview';
import { ReplyInput, ReplyItem, UnsentReply } from '@/components/QuestionResultContainer';
import { COMMENT_REPLY_MAX_LENGTH } from '@/constants/review/CommentReply';
import { ReviewType } from '@/constants/review/CycleInstance';
import { repliesClosedCopy } from '@/constants/review/Feedback360Settings';
import { isAnonymousFeedback } from '@/constants/review/FeedbackPrivacy';
import { useAmelioTranslations } from '@/hooks';
import { MOBILE_WIDTH } from '@/hooks/utils/useWindowsDimensions';

interface CommentThreadCardProps {
    thread: CommentThread;
    /** The evaluation the thread belongs to: its mode, and who wrote the comments. */
    evaluation: ReviewAnswerCommentThreads;
    /** Where this question stands among the commented ones; absent when it is the only one. */
    position?: { current: number; total: number };
    onReply: (content: string) => Promise<void>;
    isReplying: boolean;
    /** A reply refused because replies were turned off as it was sent: kept on screen once the field is gone. */
    unsentReply?: string;
}

/**
 * The server sends the name it assembled, and the results sign with first and last names joined by
 * a space: split on the last space, the name reads back exactly as sent.
 */
const authorOf = ({
    authorName: name,
    authorPictureUrl: pictureUrl,
}: CommentThreadReply): CommentAuthor | null => {
    if (!name) {
        return null;
    }

    const lastSpace = name.lastIndexOf(' ');
    if (lastSpace < 0) {
        return { firstName: name, lastName: '', profilePictureUrl: pictureUrl };
    }

    return {
        firstName: name.slice(0, lastSpace),
        lastName: name.slice(lastSpace + 1),
        profilePictureUrl: pictureUrl,
    };
};

const toReplyComment = (reply: CommentThreadReply, isAnonymous: boolean): QuestionComment => ({
    id: reply.id,
    user: authorOf(reply),
    text: reply.content,
    timestamp: reply.createdAt ? new Date(reply.createdAt) : undefined,
    isAnonymousRespondent: isAnonymous && reply.authorIsRespondent,
});

/**
 * One commented question: what the respondent wrote, the replies it received as the results show them,
 * then the respondent's own reply, or why none can be added any more.
 * Below the field, a 360 respondent sees how their reply will be signed; a self-evaluation has no mode
 * to tell of.
 */
export const CommentThreadCard = ({
    thread,
    evaluation,
    position,
    onReply,
    isReplying,
    unsentReply,
}: CommentThreadCardProps) => {
    const { t, i18n } = useAmelioTranslations();
    const isPhone = useViewportBelow(MOBILE_WIDTH);
    const dataTestId = `comment-thread-${thread.reviewQuestionAnswerId}`;
    const is360 = evaluation.reviewType === ReviewType.EVALUATION_360;
    const isAnonymous = isAnonymousFeedback(evaluation.feedbackPrivacy);
    // A phone has room for the send icon alone; a tablet and a computer, for a button that says what it does.
    const submitAs = isPhone ? 'icon' : 'button';
    // The server dates no comment of an anonymous 360: when it was written could tell who wrote it.
    const writtenAgo =
        thread.commentedAt &&
        getRelativeTime({ date: new Date(thread.commentedAt), language: i18n.language });

    return (
        <Card
            className={'flex flex-col gap-6 rounded-3xl bg-white'}
            padding={'lg'}
            dataTestId={dataTestId}
        >
            <div className={'flex flex-col gap-2'}>
                {position && (
                    <Text color={Colors.BASE_600}>
                        {t('reviewAnswer.commentThread.questionPosition', position)}
                    </Text>
                )}

                <Heading
                    level={2}
                    weight={'semibold'}
                    color={Colors.BASE}
                    className={'text-2xl'}
                >
                    {thread.questionTitle}
                </Heading>
            </div>

            <blockquote
                className={'border-secondary flex flex-col gap-1 border-s-4 ps-4'}
                data-testid={`${dataTestId}-comment`}
            >
                <Text
                    weight={'semibold'}
                    color={Colors.SECONDARY}
                >
                    {writtenAgo
                        ? `${t('reviewAnswer.commentThread.whatYouWrote')} · ${writtenAgo}`
                        : t('reviewAnswer.commentThread.whatYouWrote')}
                </Text>

                <Text
                    color={Colors.BASE}
                    className={'text-lg leading-relaxed break-words whitespace-pre-wrap'}
                >
                    {thread.comment}
                </Text>
            </blockquote>

            {thread.replies.length > 0 && (
                <div className={'flex flex-col gap-4'}>
                    {thread.replies.map((reply) => (
                        <ReplyItem
                            key={reply.id}
                            reply={toReplyComment(reply, isAnonymous)}
                            dataTestId={`${dataTestId}-reply-${reply.id}`}
                        />
                    ))}
                </div>
            )}

            {evaluation.commentRepliesEnabled ? (
                <div className={'border-base-100 flex flex-col gap-3 border-t pt-6'}>
                    <ReplyInput
                        onSubmit={onReply}
                        isSubmitting={isReplying}
                        placeholder={t('reviewAnswer.commentThread.replyPlaceholder')}
                        ariaLabel={t('reviewAnswer.commentThread.replyLabel')}
                        submitLabel={t('reviewAnswer.commentThread.send')}
                        submitAs={submitAs}
                        rows={submitAs === 'button' ? 3 : 1}
                        maxLength={COMMENT_REPLY_MAX_LENGTH}
                        dataTestId={`${dataTestId}-reply-form`}
                    />

                    {is360 && (
                        <AuthorSignaturePreview
                            label={t('reviewAnswer.commentThread.signedBy')}
                            authorName={evaluation.respondentName}
                            authorPictureUrl={evaluation.respondentPictureUrl}
                            isAnonymous={isAnonymous}
                            note={
                                isAnonymous
                                    ? t('reviewAnswer.commentThread.signedNameNeverShown')
                                    : undefined
                            }
                            dataTestId={`${dataTestId}-signed-as`}
                        />
                    )}
                </div>
            ) : (
                <>
                    <Text
                        size={'small'}
                        color={Colors.BASE_600}
                    >
                        {t(repliesClosedCopy(is360))}
                    </Text>

                    {unsentReply && (
                        <UnsentReply
                            text={unsentReply}
                            dataTestId={`${dataTestId}-unsent-reply`}
                        />
                    )}
                </>
            )}
        </Card>
    );
};
